import {
  dimensionKey, metricKey, TIME_GRAINS, type DimensionSelection, type Filter, type Row, type SemanticQueryInput, type TimeGrain, type TimeRange,
  type Visualization, type WidgetDefinitionInput,
} from "@cd/core";
import { canFilterBy, canZoomInto, groupLabel, type PickedValue } from "@cd/viz";
import { DATES_V, isDay, zoomTo, type ZoomGrain } from "./dateZoom";
import type { TimeSelection } from "./time";

type Value = Row[string];

/** One field value a breakdown is narrowed to. */
export interface DrillValue {
  ref: string;
  /** The field's label, e.g. "Category". */
  label: string;
  value: Value;
  valueLabel: string;
}

/** One click in a breakdown: what it narrowed to, and the field it's then split by. */
export interface DrillStep {
  /** Field values from the click: a bar for Cocktails · Ana narrows to both. */
  values: DrillValue[];
  /** A clicked date bucket, kept to the dates the widget showed (as a zoom is). */
  dates?: { range: TimeRange; label: string };
  /** The field this step is split by; unset until the viewer picks one. */
  by?: DimensionSelection;
}

/**
 * "Break down … by…": a temporary widget in a side panel, from a widget's metrics narrowed by each
 * step's clicked values. Page state only, like the chips: it lives in the URL (`d`), never saved.
 */
export interface Drill {
  /** Always `DATES_V`; links without it are dropped. */
  v: typeof DATES_V;
  /** The widget it started from, whose metrics and own filters it keeps. */
  widgetId: string;
  steps: DrillStep[];
}

/** Most steps a breakdown can take, and values one click can add; keeps the URL bounded. */
export const MAX_DRILL_STEPS = 8;
const MAX_STEP_VALUES = 5;
/** Bars a breakdown shows when split by a field that isn't a time axis. */
export const DRILL_LIMIT = 25;

/**
 * The step a click makes, or undefined when nothing in it can be narrowed to. Date buckets are cut
 * to `shown`, the dates the widget showed, so the number clicked is the number the panel starts
 * from. Grains that aren't a span of dates (hour, day of week) can't be narrowed to and are left out.
 */
export function stepFrom(picks: PickedValue[], shown: TimeRange | undefined): DrillStep | undefined {
  const values = picks.filter((p) => canFilterBy(p.column)).slice(0, MAX_STEP_VALUES).map((p): DrillValue => ({
    ref: p.column.key, label: p.column.label, value: p.value, valueLabel: groupLabel(p.column, p.value),
  }));
  const date = picks.find((p) => canZoomInto(p.column));
  const zoom = date && zoomTo(date.value, date.column.grain as ZoomGrain, shown);
  if (!values.length && !zoom) return undefined;
  return { values, ...(zoom ? { dates: { range: zoom.range, label: zoom.label } } : {}) };
}

/** "Cocktails · Ana", "Oct 2026". */
export const stepLabel = (s: DrillStep) => [...s.values.map((v) => v.valueLabel), ...(s.dates ? [s.dates.label] : [])].join(" · ");

/** The field the breakdown is split by now: the last step's. */
export const drillBy = (d: Drill) => d.steps.at(-1)?.by;

/** One query filter per narrowed value. An empty value is `is_null`, as for chips. */
export function drillFilters(d: Drill): Filter[] {
  return d.steps.flatMap((s) => s.values.map((v): Filter => (v.value === null ? { ref: v.ref, op: "is_null", values: [] } : { ref: v.ref, op: "in", values: [v.value] })));
}

/** The dates the breakdown is narrowed to, if any step clicked a date: the last, since each is inside the one before. */
export const drillRange = (d: Drill) => [...d.steps].reverse().find((s) => s.dates)?.dates?.range;

/** Splits the breakdown by `by` at its last step. A new field picked replaces the URL entry rather than adding one. */
export const withBy = (d: Drill, by: DimensionSelection): Drill => ({ ...d, steps: d.steps.map((s, i) => (i === d.steps.length - 1 ? { ...s, by } : s)) });

/** Adds a click from the panel: narrowed to its values, then waiting for a field to split by. */
export const addStep = (d: Drill, step: DrillStep): Drill =>
  d.steps.length >= MAX_DRILL_STEPS ? d : { ...d, steps: [...d.steps, step] };

/** Back to step `i`, split as it was. */
export const backTo = (d: Drill, i: number): Drill => ({ ...d, steps: d.steps.slice(0, i + 1) });

/** Whether a field has been narrowed to one value, so splitting by it would give one bar. */
export const narrowedRefs = (d: Drill) => new Set(d.steps.flatMap((s) => s.values.map((v) => v.ref)));

/**
 * The grain a time field splits by: hours inside a single day, else days, else the field's first.
 * `range` is the breakdown's dates, when they're known on the browser.
 */
export function defaultGrain(grains: readonly string[] | undefined, range: TimeRange | undefined): TimeGrain | undefined {
  const allowed = (grains ?? []).filter((g): g is TimeGrain => (TIME_GRAINS as readonly string[]).includes(g));
  const oneDay = range && Date.parse(`${range.end.slice(0, 10)}T00:00:00Z`) - Date.parse(`${range.start.slice(0, 10)}T00:00:00Z`) <= 0;
  if (oneDay && allowed.includes("hour")) return "hour";
  return allowed.includes("day") ? "day" : allowed[0];
}

/** A line for a time axis (not day of week, which is categories), bars for everything else. */
export const drillViz = (by: DimensionSelection): Visualization =>
  ({ type: by.grain && by.grain !== "day_of_week" ? "line" : "bar", config: {} });

/**
 * The widget's own query, narrowed by every step and split by the current field: a time axis in
 * order, anything else largest first and capped at `DRILL_LIMIT`. Comparisons are left out, since
 * a breakdown shows one period; an average per day is kept, so the numbers match the widget's.
 */
function baseQuery(source: SemanticQueryInput, d: Drill, by: DimensionSelection): SemanticQueryInput {
  const key = dimensionKey(by);
  const first = source.metrics[0]!;
  const timeAxis = !!by.grain && by.grain !== "day_of_week";
  return {
    pack: source.pack,
    metrics: source.metrics,
    dimensions: [by],
    filters: [...(source.filters ?? []), ...drillFilters(d)],
    sort: timeAxis ? [{ key, dir: "asc" }] : [{ key: metricKey(first), dir: "desc" }],
    ...(timeAxis ? {} : { limit: DRILL_LIMIT }),
    averagePer: source.averagePer,
    // Its formulas still name the widget's inputs, so a saved breakdown gets the same boxes.
    ...(source.inputs?.length ? { inputs: source.inputs } : {}),
  };
}

/** What the panel queries: undefined until a field is picked. A clicked date replaces the page's dates. */
export function drillQuery(source: SemanticQueryInput, d: Drill, time: TimeSelection): SemanticQueryInput | undefined {
  const by = drillBy(d);
  if (!by) return undefined;
  const range = drillRange(d);
  return { ...baseQuery(source, d, by), ...(range ? { timeRange: range } : time) };
}

/**
 * The widget "Save as widget" adds: the narrowed values written into its own filters. Never the
 * page's chips, and never a clicked date, since a dashboard's range replaces a widget's.
 */
export function drillDefinition(source: SemanticQueryInput, d: Drill, title: string, viz: Visualization): WidgetDefinitionInput | undefined {
  const by = drillBy(d);
  if (!by) return undefined;
  return { title: title.trim() || "Untitled widget", query: baseQuery(source, d, by), visualization: viz };
}

/** "Net sales by Server (Cocktails, Friday)", from the metrics' names. */
export function drillTitle(metrics: string, d: Drill, byLabel: string): string {
  const narrowed = d.steps.flatMap((s) => s.values.map((v) => v.valueLabel));
  return `${metrics} by ${byLabel}${narrowed.length ? ` (${narrowed.join(", ")})` : ""}`.slice(0, 120);
}

const REF = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
const isText = (v: unknown, max: number): v is string => typeof v === "string" && v.length <= max;
const isValue = (v: unknown): v is Value =>
  v === null || typeof v === "boolean" || (typeof v === "number" && Number.isFinite(v)) || isText(v, 200);

function isStep(s: unknown): s is DrillStep {
  if (!s || typeof s !== "object") return false;
  const c = s as Record<string, unknown>;
  const values = c.values;
  if (!Array.isArray(values) || values.length > MAX_STEP_VALUES) return false;
  const valuesOk = values.every((v) => {
    const x = v as Record<string, unknown> | null;
    return !!x && typeof x === "object" && isText(x.ref, 200) && REF.test(x.ref) && isText(x.label, 120) && isValue(x.value) && isText(x.valueLabel, 200);
  });
  const dates = c.dates as Record<string, unknown> | undefined;
  const range = dates?.range as Record<string, unknown> | undefined;
  const datesOk = dates === undefined
    || (!!range && isDay(range.start) && isDay(range.end) && range.start <= range.end && isText(dates.label, 60));
  const by = c.by as Record<string, unknown> | undefined;
  const byOk = by === undefined
    || (typeof by === "object" && by !== null && isText(by.ref, 200) && REF.test(by.ref)
      && (by.grain === undefined || (TIME_GRAINS as readonly unknown[]).includes(by.grain)));
  return valuesOk && datesOk && byOk && (values.length > 0 || dates !== undefined);
}

/** The `d` search param. A hand-edited or stale one is dropped, never an error. */
export function parseDrill(d: unknown): Drill | undefined {
  if (!d || typeof d !== "object") return undefined;
  const c = d as Record<string, unknown>;
  if (!isText(c.widgetId, 64) || !c.widgetId) return undefined;
  if (c.v !== DATES_V) return undefined; // saved before dates were inclusive; see DATES_V
  const steps = c.steps;
  if (!Array.isArray(steps) || !steps.length || steps.length > MAX_DRILL_STEPS || !steps.every(isStep)) return undefined;
  return { v: DATES_V, widgetId: c.widgetId, steps };
}
